import { create } from 'zustand';

/**
 * T2376 — ponovna prijava nad odprto stranjo, ko seja poteče med delom.
 *
 * Prej je padel refresh pomenil `clearAuth()` in `ProtectedRoute` je uporabnika
 * brez besede vrgel na `/login` — odprt popis, sekcija in neshranjen vnos so
 * izginili. Zdaj interceptor (`services/api.ts`) zahtevo, ki je padla z 401,
 * **zadrži**: `request()` odpre okno za ponovno prijavo in vrne obljubo, ki se
 * izpolni ob uspešni prijavi (zahteva se nato ponovi) ali zavrne ob preklicu.
 *
 * Shramba ne uvaža ne `api` ne `authStore` — tako jo lahko uvozi kdorkoli brez
 * krožne odvisnosti, interceptor pa ostane neodvisen od Reacta.
 */

export class ReauthCancelledError extends Error {
  constructor() {
    super('reauth_cancelled');
    this.name = 'ReauthCancelledError';
  }
}

interface Waiter {
  resolve: () => void;
  reject: (err: unknown) => void;
}

// Obljube čakajočih zahtev živijo zunaj stanja: niso del izrisa in ne smejo
// sprožati ponovnih izrisov.
let waiters: Waiter[] = [];

interface ReauthState {
  open: boolean;
  /** Uporabnik, čigar seja je potekla — okno sme prijaviti samo njega. */
  userId: number | null;
  email: string | null;
  /**
   * T2689: števec uspešnih ponovnih prijav. Naročniki (WebSocket kanali) ob
   * njegovi spremembi vedo, da je seja spet živa, in se povežejo znova.
   */
  renewals: number;
  request: (user: { id: number; email: string }) => Promise<void>;
  succeed: () => void;
  cancel: () => void;
}

export const useReauthStore = create<ReauthState>((set) => ({
  open: false,
  userId: null,
  email: null,
  renewals: 0,

  request: (user) => {
    set({ open: true, userId: user.id, email: user.email });
    return new Promise<void>((resolve, reject) => {
      waiters.push({ resolve, reject });
    });
  },

  succeed: () => {
    const pending = waiters;
    waiters = [];
    set((s) => ({ open: false, userId: null, email: null, renewals: s.renewals + 1 }));
    pending.forEach((w) => w.resolve());
  },

  cancel: () => {
    const pending = waiters;
    waiters = [];
    set({ open: false, userId: null, email: null });
    pending.forEach((w) => w.reject(new ReauthCancelledError()));
  },
}));
